import { addBreadcrumb, captureException, captureMessage, getClient } from "@sentry/react";
import {
    AudioControlAction,
    AUDIO_CONTROL_CATEGORY,
    AUDIO_CONTROL_VERIFY_CATEGORY,
    EPSILON,
    IAudioSnapshot,
    IAudioControlContext,
} from "./audio-sentry-instrumentation.constants";

const hasSentryClient = () => Boolean(getClient());

/**
 * Get the seekable range of the audio element.
 * @param audio - The audio element to get the seekable range of.
 * @returns The seekable range of the audio element.
 */
const getSeekableRange = (audio: HTMLAudioElement) => {
    if (audio.seekable.length === 0) {
        return {
            seekableStart: null,
            seekableEnd: null,
        };
    }

    try {
        return {
            seekableStart: audio.seekable.start(0),
            seekableEnd: audio.seekable.end(0),
        };
    } catch (error: unknown) {
        if (hasSentryClient()) {
            captureException(error instanceof Error ? error : new Error(String(error)), {
                tags: {
                    feature: "audio",
                    audio_diagnostic: "seekable_range_read",
                },
                extra: {
                    seekableRangeCount: audio.seekable.length,
                    currentTime: audio.currentTime,
                    readyState: audio.readyState,
                    networkState: audio.networkState,
                },
            });
        }

        return {
            seekableStart: null,
            seekableEnd: null,
        };
    }
};

/**
 * Build an audio snapshot from the audio element.
 * @param audio - The audio element to build the snapshot from.
 * @returns The audio snapshot.
 */
export const buildAudioSnapshot = (audio: HTMLAudioElement): IAudioSnapshot => {
    const { seekableStart, seekableEnd } = getSeekableRange(audio);

    return {
        currentTime: audio.currentTime,
        duration: Number.isFinite(audio.duration) ? audio.duration : null,
        paused: audio.paused,
        readyState: audio.readyState,
        networkState: audio.networkState,
        seekableRangeCount: audio.seekable.length,
        seekableStart,
        seekableEnd,
        errorCode: audio.error?.code || null,
        hasSrc: Boolean(audio.src),
    };
};

/**
 * Check if the audio control is a suspect reset to start.
 * @param context - The audio control context.
 * @returns True if the audio control is a suspect reset to start, false otherwise.
 */
const isSuspectResetToStart = ({ action, payload, before, afterSync }: IAudioControlContext) => {
    switch (action) {
        case AudioControlAction.SEEK:
            return payload >= 2 && before.currentTime >= 1 && afterSync.currentTime <= EPSILON;
        case AudioControlAction.SKIP_FORWARD:
            return payload >= 2 && before.currentTime >= 1 && afterSync.currentTime < before.currentTime - EPSILON;
        case AudioControlAction.SKIP_BACKWARD:
            return before.currentTime > payload + 5 && afterSync.currentTime <= EPSILON;
        default:
            return false;
    }
};

/**
 * Record a skipped control without an audio reference.
 * @param action - The audio control action that was skipped.
 */
export const recordSkippedControlWithoutAudioRef = (action: AudioControlAction) => {
    if (!hasSentryClient()) {
        return;
    }

    addBreadcrumb({
        category: AUDIO_CONTROL_CATEGORY,
        level: "info",
        message: "Audio control skipped because audioRef was unavailable",
        data: {
            action,
            reason: "missing_audio_ref",
        },
    });
};

/**
 * Record an audio control breadcrumb.
 * @param context - The audio control context.
 */
export const recordAudioControlBreadcrumb = (context: IAudioControlContext) => {
    if (!hasSentryClient()) {
        return;
    }

    addBreadcrumb({
        category: AUDIO_CONTROL_CATEGORY,
        level: "info",
        message: `Audio ${context.action} control triggered`,
        data: context,
    });
};

/**
 * Report a suspect seek to Sentry.
 * @param context - The audio control context.
 */
export const reportSuspectSeekToSentry = (context: IAudioControlContext) => {
    if (!hasSentryClient() || !isSuspectResetToStart(context)) {
        return;
    }

    captureMessage("Audio control appears to reset currentTime to start", {
        level: "warning",
        tags: {
            audio_control: context.action,
        },
        extra: {
            payload: context.payload,
            before: context.before,
            afterSync: context.afterSync,
        },
    });
};

/**
 * Record an audio control async verification.
 * @param action - The audio control action.
 * @param payload - The payload of the audio control.
 * @param afterAsync - The after async snapshot.
 */
export const recordAudioControlAsyncVerification = ({
    action,
    payload,
    afterAsync,
}: {
    readonly action: AudioControlAction;
    readonly payload: number;
    readonly afterAsync: IAudioSnapshot;
}) => {
    if (!hasSentryClient()) {
        return;
    }

    addBreadcrumb({
        category: AUDIO_CONTROL_VERIFY_CATEGORY,
        level: "info",
        message: `Audio ${action} control async verification`,
        data: {
            action,
            payload,
            afterAsync,
        },
    });
};

/**
 * Schedule an audio control verification.
 * @param action - The audio control action.
 * @param payload - The payload of the audio control.
 * @param audio - The audio element to verify.
 */
export const scheduleAudioControlVerification = ({
    action,
    payload,
    audio,
}: {
    readonly action: AudioControlAction;
    readonly payload: number;
    readonly audio: HTMLAudioElement;
}) => {
    if (!hasSentryClient()) {
        return;
    }

    const verify = () => {
        if (!hasSentryClient()) {
            return;
        }

        recordAudioControlAsyncVerification({
            action,
            payload,
            afterAsync: buildAudioSnapshot(audio),
        });
    };

    if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(verify);
        return;
    }

    queueMicrotask(verify);
};
